import React, { useEffect, useRef } from 'react';
import { CheckboxProps } from '../../../types/DesignSystem/Checkbox';
import Typography from '../Typography/Typography';
import { FOCUS_RING } from '../focusRing';
import { useMergedRef } from '../useMergedRef';
import { Check } from '../Iconography/Icons';
import { withClassName } from '../cn';
import { useControllableState } from '../useControllableState';

/**
 * Checkbox.
 *
 * Built on a real `<input type="checkbox">`. The previous implementation was a
 * `<div role="checkbox">` with an `onClick` and no `tabIndex`, which meant a
 * keyboard user could neither reach nor toggle it — the browser gives all of that
 * away for free once the control is a native input.
 *
 * The input is styled directly (`appearance-none`) and the check/dash glyph sits
 * over it in a `pointer-events-none` overlay, so every click lands on the input
 * itself. That matters: `ComponentLibrary`'s multiselect row does
 * `closest('[role="checkbox"]')` on the click target to avoid double-toggling,
 * which is why the redundant explicit `role` below is load-bearing.
 * See `okf/design-system/e2e.md`.
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox({
  label,
  labelClamp = true,
  hideLabel = false,
  checked: checkedProp,
  defaultChecked,
  onChange,
  disabled = false,
  indeterminate = false,
  className = '',
  size = 'md',
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  ...rest
}, forwardedRef) {
  const inputRef = useRef<HTMLInputElement>(null);
  // The internal ref stays — `indeterminate` is written to the node below — so the
  // caller's ref has to be merged rather than substituted.
  const setInputRef = useMergedRef<HTMLInputElement>(inputRef, forwardedRef);
  /* § 357 — uncontrolled only with `defaultChecked` and no `checked`; otherwise this is
     the controlled box it always was. */
  const [checked, setChecked, uncontrolled] = useControllableState(checkedProp, defaultChecked, false);
  const boxSize = size === 'sm' ? 'tw-w-4 tw-h-4' : 'tw-w-5 tw-h-5';
  /* The tick's render size in px — 14 in the 20px box, 10 in the 16px one (the same
     two values the old `tw-w-3.5` / `tw-w-2.5` classes stated). A number, because the
     set's `Check` sizes by prop (§ 244); see the § 295 note on the mark below. */
  const checkPx = size === 'sm' ? 10 : 14;
  const dashSize = size === 'sm' ? 'tw-w-2' : 'tw-w-2.5';
  /**
   * `md`'s 20px box already matches `body-primary`'s 20px line box, so the two
   * centre on each other with no nudge. `sm`'s 16px box needs 2px to sit on the
   * same centre line. The old unconditional `tw-mt-0.5` pushed every `md` box 2px
   * below its own label.
   */
  const boxNudge = size === 'sm' ? 'tw-mt-0.5' : '';
  /** Native checkboxes show mixed in preference to checked; match that. */
  const showDash = indeterminate;
  const showCheck = checked && !indeterminate;
  const isOn = checked || indeterminate;

  // `indeterminate` is a DOM property with no matching HTML attribute, so React
  // cannot set it from JSX — it has to be written to the node.
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <label
      className={withClassName(`tw-flex tw-items-start tw-gap-3 ${
        disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
      }`, className)}
    >
      <span
        className={`tw-relative tw-flex-shrink-0 ${boxNudge}`}
        data-testid={dataTestId ? `${dataTestId}-box` : undefined}
      >
        <input
          /* rest first — see the rule in Button.tsx. */
          {...rest}
          ref={setInputRef}
          type="checkbox"
          role="checkbox"
          /* While controlled, the caller's `checked` reaches the input as passed —
             `undefined` included, which a JS call site can send — so the native
             control's mode is exactly what it was. */
          checked={uncontrolled ? checked : checkedProp}
          disabled={disabled}
          onChange={(event) => {
            setChecked(event.target.checked);
            onChange?.(event.target.checked, event);
          }}
          /*
            No `?? label`. The control is already inside a `<label>` that contains
            that text, so the browser derives the accessible name from it — and an
            `aria-label` OVERRIDES that text rather than adding to it, which is how
            the accessible name and the visible name come to differ (WCAG 2.5.3).
            What remains is honoured only when a caller explicitly asks.
          */
          aria-label={ariaLabel}
          data-testid={dataTestId}
          /*
            `forced-colors:tw-appearance-auto` (§ 224): Windows High Contrast strips
            the fill this control's state lives in, so a checked box rendered BLANK
            there. Handing the drawing back to the OS (`appearance: auto`) is the
            canonical fix — the native glyph is exactly what that mode's palette is
            built to draw; the custom overlay hides itself in the same mode below.
          */
          className={`${boxSize} tw-block tw-m-0 tw-appearance-none forced-colors:tw-appearance-auto tw-border tw-rounded-ctl tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${
            // Filled when on, with a white glyph: a hollow box with a thin blue
            // tick reads weak, and the fill is what makes the state legible at
            // 20px. 1px border rather than 2px — grey-500 is dark enough now that
            // 2px reads heavy against a 14px glyph.
            //
            // § 227 — DISABLED recedes by token, not the `tw-opacity-50` wash the
            // box wore (the treatment § 123 reversed for Toggle: opacity multiplies
            // against whatever ground sits under it, and it dimmed the box while the
            // label beside it stayed full ink). Checked-and-locked is Toggle's exact
            // disabled pair — `ink-disabled` fill under an `ink-secondary` glyph
            // (3.76:1 on it) — greyscale, hue dropped, the mark still readable.
            // Unchecked-and-locked recedes to the disabled FIELD chrome
            // (`surface-sunken` + `edge`, fieldChrome.ts), so a locked empty box
            // still reads as a box rather than as a stain.
            disabled
              ? isOn
                ? 'tw-bg-ink-disabled tw-border-ink-disabled'
                : 'tw-bg-surface-sunken tw-border-edge'
              : isOn
                ? 'tw-bg-accent-bold tw-border-accent-bold'
                : 'tw-bg-surface tw-border-edge-strong'
          } ${
            disabled
              ? 'tw-cursor-not-allowed'
              : isOn
                ? 'tw-cursor-pointer'
                : // hover only while empty — tinting a filled box light blue is wrong
                  'tw-cursor-pointer hover:tw-bg-accent-tint hover:tw-border-accent'
          } ${FOCUS_RING}`}
        />
        <span
          aria-hidden="true"
          /* Hidden under forced colors (§ 224) — the native input draws its own
             glyph there, and this one over it would double-strike the mark. */
          className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none forced-colors:tw-hidden"
        >
          {/* § 219 — the mark POPS (140ms spring scale-in): the check and dash
              mount conditionally, so the keyframe entrance runs exactly when the
              state flips on. Entrance-only, and motion-safe like everything else. */}
          {/* § 295 — the SET's tick, not a private one. This drew its own FILLED
              heroicons check (a solid path on a 20 grid) while Toggle, Dropdown,
              Combobox and Toast all draw the stroked 16-grid `Check` — so a checkbox
              and a toggle in one form showed two hands. Same mark now, at the two
              sizes the box always used; `aria-hidden` and the mark-pop ride the spread.
              § 227 — the disabled glyph is `ink-secondary`, not white: Toggle's § 123
              arrangement, because the fill under it IS `ink-disabled`. */}
          {/* § 297 — the tick DRAWS ON (200ms, swift) instead of popping: the stroke is
              laid from its first point to its last, which is what the mark IS. The
              dash below keeps mark-pop — a bar has no direction to draw in. Both
              motion-safe; under reduced motion the tick simply appears. */}
          {showCheck && (
            <Check
              size={checkPx}
              aria-hidden="true"
              className={`${disabled ? 'tw-text-ink-secondary' : 'tw-text-ink-inverse'} motion-safe:tw-animate-tick-draw`}
            />
          )}
          {showDash && <span className={`tw-block ${dashSize} tw-h-0.5 ${disabled ? 'tw-bg-ink-secondary' : 'tw-bg-ink-inverse'} motion-safe:tw-animate-mark-pop`} />}
        </span>
      </span>
      <span
        /* § 355 — hidden, not removed: text inside the `<label>` stays the box's name. */
        className={hideLabel ? 'tw-sr-only' : 'tw-min-w-0 tw-flex-1'}
        title={typeof label === 'string' ? label : undefined}
      >
        <Typography
          variant="body-primary"
          /* § 346 — a `span`: `body-primary` defaults to `<p>`, and a `<p>` inside a
             `<label>` is invalid. `block` keeps the box it had, except when clamped —
             `line-clamp` is already a block-level box (`display: -webkit-box`), and
             Tailwind emits `.tw-block` after it, so adding it would undo the clamp. */
          as="span"
          /* § 227 — the label recedes WITH its control (Toggle's rule): a locked box
             beside a full-ink label inverts the hierarchy — the one part you cannot
             act on was the loudest part of the row. */
          className={`${disabled ? 'tw-text-ink-disabled' : 'tw-text-ink'} tw-break-words ${labelClamp ? 'tw-line-clamp-2' : 'tw-block'}`}
        >
          {label}
        </Typography>
      </span>
    </label>
  );
});

export default Checkbox;
